/* ════════════════════════════════════════════════════════════════════════
   CART DRAWER — Performance Boutique · build per tema EXPANSE (Archetype)
   Posiziona in: assets/cart-drawer.js

   Element custom: <pb-cart-drawer>  (rinominato per non collidere col
   <cart-drawer> nativo di Expanse v7).

   Strategia: REPLACEMENT autosufficiente.
   - Intercetto io l'add-to-cart in CAPTURE + stopImmediatePropagation, così
     precedo e blocco l'handler nativo di Expanse → niente drawer nativo,
     niente redirect a /cart, a prescindere dal "Cart type" impostato.
   - Faccio l'add/change via Ajax API, renderizzo la mia sezione via Section
     Rendering API, e lancio `cart:build` per risincronizzare il count header.
   - `ajaxProduct:added` resta come fallback per quick-add/quick-view che non
     passano da un <form> standard.

   API pubblica:  window.PBCartDrawer.open() / .close() / .refresh()
   ════════════════════════════════════════════════════════════════════════ */

const PB_TAG = 'cart-drawer';
const GIFT_PROP = '_discount_trigger';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel  = this.querySelector('[data-cd-panel]');
    this.body   = this.querySelector('[data-cd-body]');
    this.recsEl = this.querySelector('[data-cd-recommendations]');
    this.sectionId = this.dataset.sectionId; // = "cart-drawer" (nome file sezione)
    this.urls = {
      change: this.dataset.cartChangeUrl,
      add:    this.dataset.cartAddUrl,
      recs:   this.dataset.recommendationsUrl,
    };
    this.lastOpener = null;
    this.lastAddedProductId = null;
    this.fetching = false;
    this._skipNextUpdate = false;
    this._justAdded = 0;
  }

  connectedCallback() {
    this.bindEvents();
    window.PBCartDrawer = this;
    document.addEventListener('pb:cart:open', () => this.open());
  }

  /* ─────────────── Eventi UI ─────────────── */
  bindEvents() {
    this.querySelectorAll('[data-cd-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.querySelector('[data-cd-overlay]')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.handleTab(e);
      if (e.key === 'Enter' && e.target.matches('[data-cd-discount-input]')) {
            e.preventDefault();
            this.applyDiscount(e.target.value.trim());
        }
    });

    this.panel.addEventListener('click', (e) => {
      const step     = e.target.closest('[data-cd-step]');
      const remove   = e.target.closest('[data-cd-remove]');
      const add      = e.target.closest('[data-cd-add]');
      const checkout = e.target.closest('[data-cd-checkout]');
      const prev = e.target.closest('[data-cd-prev]');
      const next = e.target.closest('[data-cd-next]');
      const dot  = e.target.closest('[data-cd-dot]');
      const dApply  = e.target.closest('[data-cd-discount-apply]');
      const dRemove = e.target.closest('[data-cd-discount-remove]');
      if (step)     return this.onStep(step);
      if (remove)   return this.changeLine(remove.dataset.key, 0, remove.closest('[data-cd-item]'));
      if (add)      return this.onUpsellAdd(add);
      if (checkout) return this.onCheckout();
      if (prev) return this.slideUpsell(-1);
      if (next) return this.slideUpsell(1);
      if (dot)  return this.slideToUpsell(parseInt(dot.dataset.cdDot, 10));
      if (dApply)  { const i = this.querySelector('[data-cd-discount-input]'); return this.applyDiscount(i ? i.value.trim() : ''); }
      if (dRemove) return this.removeDiscount();
    });

    this.panel.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-cd-qty]');
    if (qty) {
        this.changeLine(qty.dataset.key, Math.max(0, parseInt(qty.value, 10) || 0), qty.closest('[data-cd-item]'));
        return;
    }
    const variant = e.target.closest('[data-cd-variant]');
    if (variant) this.onVariantChange(variant);
    });
  }

  onStep(btn) {
    const item  = btn.closest('[data-cd-item]');
    const input = item.querySelector('[data-cd-qty]');
    const next  = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.cdStep, 10));
    this.changeLine(btn.dataset.key, next, item);
  }

  /* ─────────────── Apertura / chiusura ─────────────── */
  open() {
    this.lastOpener = document.activeElement;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.lockScroll();
    this.refreshFromServer();
    this.syncGifts();
    this.fetchRecommendations();
    setTimeout(() => this.querySelector('[data-cd-close]')?.focus(), 320);
  }

  close() {
    this.classList.remove('is-open');
    this.unlockScroll();
    const done = () => {
      this.hidden = true;
      this.panel.removeEventListener('transitionend', done);
      if (this.lastOpener?.focus) this.lastOpener.focus();
    };
    this.panel.addEventListener('transitionend', done);
  }

  lockScroll() {
    this.scrollY = window.scrollY;
    document.body.style.cssText = `position:fixed;top:-${this.scrollY}px;left:0;right:0;width:100%;`;
  }
  unlockScroll() {
    document.body.style.cssText = '';
    if (this.scrollY != null) window.scrollTo(0, this.scrollY);
  }

  handleTab(e) {
    const focusable = [...this.panel.querySelectorAll(
      'a[href],button:not([disabled]),input,textarea,[tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  setLoading(state) { this.classList.toggle('is-loading', state); }

  /* ─────────────── Sincronizzazione ─────────────── */
  async refreshFromServer() {
    try {
      const res  = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
      const data = await res.json();
      this.applySections(data); // GET → { 'cart-drawer': html }
    } catch (err) {
      console.error('[CartDrawer] refresh', err);
    }
  }

  syncTheme() {
    this._skipNextUpdate = true;
    document.dispatchEvent(new CustomEvent('cart:build')); // Expanse aggiorna count + drawer nativo (nascosto)
  }

  /* ─────────────── Mutazioni ─────────────── */
  async changeLine(key, quantity, itemEl) {
    if (this.fetching) return;
    this.fetching = true;
    this.setLoading(true);
    if (quantity === 0 && itemEl) itemEl.classList.add('is-removing');
    try {
      const res = await fetch(this.urls.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: this.sectionId, sections_url: window.location.pathname }),
      });
      const data = await res.json();
      this.applySections(data.sections);
      if (parseInt(this.dataset.cdCount, 10) === 0) this.recsEl.innerHTML = '';
      this.syncTheme();
      this.syncGifts();
    } catch (err) {
      console.error('[CartDrawer] change', err);
      itemEl?.classList.remove('is-removing');
    } finally {
      this.fetching = false;
      this.setLoading(false);
    }
  }

  async addItems(items) {
    const res = await fetch(this.urls.add, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: this.sectionId, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || 'Errore aggiunta');
    this.applySections(data.sections);
    this.syncTheme();
    this.syncGifts();
    return data;
  }

  async syncGifts() {
  if (this._syncingGifts) return;                    // guard anti-loop
  const el = this.querySelector('[data-cd-gifts]');
  if (!el) return;
  let gifts;
  try { gifts = JSON.parse(el.textContent); } catch { return; }
  if (!gifts.length) return;

  this._syncingGifts = true;
  try {
    const cart = await (await fetch('/cart.js')).json();
    const giftLines = cart.items.filter((i) => i.properties?.[GIFT_PROP]);
    const eligible  = cart.total_price - giftLines.reduce((s, i) => s + i.final_line_price, 0);

    const toAdd = [], toRemove = [];
    for (const g of gifts) {
      const line = giftLines.find(
        (i) => i.variant_id === g.variant && i.properties[GIFT_PROP] === g.trigger
      );
      if (eligible >= g.threshold && !line) toAdd.push(g);
      else if (eligible < g.threshold && line) toRemove.push(line.key);
      else if (line && line.quantity > 1) { toRemove.push(line.key); toAdd.push(g); }
    }
    if (!toAdd.length && !toRemove.length) return;

    for (const key of toRemove) {
      await fetch(this.urls.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    }
    if (toAdd.length) {
      await fetch(this.urls.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: toAdd.map((g) => ({ id: g.variant, quantity: 1, properties: { [GIFT_PROP]: g.trigger } })),
        }),
      });
    }
    await this.refreshFromServer();
    this.syncTheme();
  } catch (err) {
    console.error('[CartDrawer] gifts', err);
  } finally {
    this._syncingGifts = false;
  }
}

  // Add da un <form> del tema (PDP / quick-add) — bypassa del tutto Expanse.
  async addFromForm(form, submitter) {
    const fd  = new FormData(form, submitter);
    const btn = submitter || form.querySelector('[type="submit"]');
    btn?.setAttribute('disabled', '');
    this._justAdded = Date.now();
    try {
      const res  = await fetch(this.urls.add, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      const data = await res.json();
      if (data.status) throw new Error(data.description || 'Errore'); // esaurito ecc.
      this.lastAddedProductId = data.product_id || data.items?.[0]?.product_id || null;
      this.open();      // open() fa refreshFromServer + fetchRecommendations
      this.syncTheme();
    } catch (err) {
      console.error('[CartDrawer] add', err);
    } finally {
      btn?.removeAttribute('disabled');
    }
  }

  initUpsell() {
  const track = this.recsEl.querySelector('[data-cd-track]');
  if (!track) return;
  const dots = [...this.recsEl.querySelectorAll('[data-cd-dot]')];
  const prev = this.recsEl.querySelector('[data-cd-prev]');
  const next = this.recsEl.querySelector('[data-cd-next]');
  const update = () => {
    const i = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= dots.length - 1;
  };
  track.addEventListener('scroll', () => { clearTimeout(this._upsellT); this._upsellT = setTimeout(update, 60); });
  update();
}

slideUpsell(dir) {
  const t = this.recsEl.querySelector('[data-cd-track]');
  if (t) t.scrollBy({ left: dir * t.clientWidth, behavior: 'smooth' });
}
slideToUpsell(i) {
  const t = this.recsEl.querySelector('[data-cd-track]');
  if (t) t.scrollTo({ left: i * t.clientWidth, behavior: 'smooth' });
}

onVariantChange(select) {
  const slide = select.closest('[data-cd-slide]');
  const opt   = select.selectedOptions[0];
  const price = slide?.querySelector('.pb-cd__slide-price');
  const add   = slide?.querySelector('[data-cd-add]');
  if (price && opt?.dataset.price) price.textContent = opt.dataset.price;
  if (add) add.dataset.variantId = select.value;
}

async onUpsellAdd(btn) {
  const slide  = btn.closest('[data-cd-slide]');
  const select = slide?.querySelector('[data-cd-variant]');
  const id = parseInt(select ? select.value : btn.dataset.variantId, 10);
  const label = btn.textContent;
  btn.setAttribute('disabled', '');
  try {
    await this.addItems([{ id, quantity: 1 }]);
    btn.classList.add('is-added');
    btn.textContent = 'Aggiunto ✓';
    setTimeout(() => this.fetchRecommendations(), 700);
  } catch (err) {
    console.error('[CartDrawer] upsell add', err);
    btn.textContent = label;
    btn.removeAttribute('disabled');
  }
}

  async onCheckout() {
    const note = this.querySelector('[data-cd-note]');
    if (note && note.value !== '') {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value }),
        });
      } catch (e) { /* prosegui comunque */ }
    }
    window.location.href = '/checkout';
  }

  updateCartCount(n) {
  // Numero del contatore
  document.querySelectorAll('[data-cart-count], .cart-link__bubble-num, .cart-count, .site-header__cart-count')
    .forEach((el) => { el.textContent = n; });
  // Bubble / dot: visibilità
  document.querySelectorAll('.cart-link__bubble, [data-cart-count-bubble], .site-header__cart-indicator')
    .forEach((el) => {
      el.classList.toggle('cart-link__bubble--visible', n > 0);
      el.classList.toggle('hide', n === 0);
    });
}

async applyDiscount(code) {
  if (!code) return;
  this.setLoading(true);
  const msg = this.querySelector('[data-cd-discount-msg]');
  try {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code, sections: this.sectionId, sections_url: window.location.pathname }),
    });
    const data  = await res.json();
    const entry = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
    if (entry && entry.applicable) {
      this.applySections(data.sections);
      this.syncTheme();
      this.syncGifts();
    } else if (msg) {
      msg.hidden = false;
      msg.textContent = entry ? 'Codice non applicabile a questi prodotti.' : 'Codice non valido.';
    }
  } catch (err) {
    console.error('[CartDrawer] discount', err);
    if (msg) { msg.hidden = false; msg.textContent = 'Errore, riprova.'; }
  } finally {
    this.setLoading(false);
  }
}

async removeDiscount() {
  this.setLoading(true);
  try {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '', sections: this.sectionId, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    this.applySections(data.sections);
    this.syncTheme();
    this.syncGifts();
  } catch (err) {
    console.error('[CartDrawer] discount remove', err);
  } finally {
    this.setLoading(false);
  }
}

  /* ─────────────── Section Rendering → DOM ─────────────── */
  applySections(sections) {
    if (!sections || !sections[this.sectionId]) return;
    const doc = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    const src = doc.querySelector(PB_TAG) || doc;

    // Items + footer: swap generico
    ['[data-cd-items]', '[data-cd-footer]'].forEach((sel) => {
      const incoming = src.querySelector(sel);
      const current  = this.querySelector(sel);
      if (current) current.innerHTML = incoming ? incoming.innerHTML : '';
    });

    // Barra: swap preservando l'animazione del fill
    const barIncoming = src.querySelector('[data-cd-bar]');
    const barCurrent  = this.querySelector('[data-cd-bar]');
    if (barCurrent) {
      const prevFill  = barCurrent.querySelector('.pb-cd__bar-fill');
      const prevWidth = prevFill ? prevFill.style.width : null;
      barCurrent.innerHTML = barIncoming ? barIncoming.innerHTML : '';
      const newFill = barCurrent.querySelector('.pb-cd__bar-fill');
      if (newFill && prevWidth != null) {
        const target = newFill.style.width;
        newFill.style.transition = 'none';
        newFill.style.width = prevWidth;
        void newFill.offsetWidth;            // reflow
        requestAnimationFrame(() => {
          newFill.style.transition = '';
          newFill.style.width = target;
        });
      }
    }

    const count = src.getAttribute('data-cd-count') || '0';
    this.dataset.cdCount = count;
    const label = this.querySelector('[data-cd-count-label]');
    if (label) label.textContent = count;
    this.classList.toggle('pb-cd--empty', parseInt(count, 10) === 0);
    this.updateCartCount(parseInt(count, 10));
  }

  /* ─────────────── Upsell ─────────────── */
  async fetchRecommendations() {
    if (!this.recsEl) return;
    const firstItem = this.querySelector('[data-cd-item]');
    const productId = this.lastAddedProductId || firstItem?.dataset.productId;
    if (!productId) { this.recsEl.innerHTML = ''; return; }

    const intent = this.dataset.upsellIntent || 'complementary';
    const limit  = this.dataset.upsellLimit || 4;
    const url = `${this.urls.recs}?section_id=cart-recommendations&product_id=${productId}&intent=${intent}&limit=${limit}`;
    try {
      const res   = await fetch(url);
      const doc   = new DOMParser().parseFromString(await res.text(), 'text/html');
      const block = doc.querySelector('[data-cd-upsell]');
      if (block) {
        const heading = this.recsEl.dataset.heading;
        const titleEl = block.querySelector('.pb-cd__upsell-title');
        if (heading && titleEl) titleEl.textContent = heading;
        this.recsEl.innerHTML = block.outerHTML;
        this.initUpsell();
      } else {
        this.recsEl.innerHTML = '';
      }
    } catch (err) {
      console.error('[CartDrawer] recommendations', err);
      this.recsEl.innerHTML = '';
    }
  }

  refresh() { this.refreshFromServer(); this.fetchRecommendations(); }
}

// Guard anti-collisione: definisci solo se il tag è libero.
if (!customElements.get(PB_TAG)) customElements.define(PB_TAG, CartDrawer);

/* ════════════════════════════════════════════════════════════════════════
   INTERCETTAZIONE GLOBALE (capture) — precede e blocca Expanse
   ════════════════════════════════════════════════════════════════════════ */

// Add-to-cart: cattura il submit PRIMA di Expanse e ne blocca l'handler.
document.addEventListener('submit', (e) => {
  const form = e.target.closest('form[action$="/cart/add"], form[action*="/cart/add"]');
  if (!form || form.hasAttribute('data-no-drawer')) return;
  const d = document.querySelector(PB_TAG);
  if (!d || d.dataset.intercept === 'false') return;
  e.preventDefault();
  e.stopImmediatePropagation(); // impedisce all'handler nativo di Expanse di girare
  d.addFromForm(form, e.submitter);
}, true); // <-- capture

// Click sull'icona carrello: apro il MIO drawer e blocco l'apertura nativa.
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-cart-toggle], a[href$="/cart"], a[href*="/cart?"]');
  if (!trigger) return;
  const d = document.querySelector(PB_TAG);
  if (!d || d.dataset.intercept === 'false') return;
  e.preventDefault();
  e.stopImmediatePropagation();
  d.open();
}, true); // <-- capture

// Fallback per quick-add/quick-view che NON passano da un <form>.
document.addEventListener('ajaxProduct:added', (evt) => {
  const d = document.querySelector(PB_TAG);
  if (!d || d.dataset.intercept === 'false') return;
  if (Date.now() - d._justAdded < 1000) return; // già gestito dalla mia intercettazione
  d.lastAddedProductId = evt.detail?.product?.id || null;
  d.open();
});

document.addEventListener('ajaxProduct:error', (evt) => {
  console.warn('[CartDrawer] ajaxProduct:error', evt.detail?.errorMessage);
});

// Carrello aggiornato altrove → rinfresco se aperto.
document.addEventListener('cart:updated', () => {
  const d = document.querySelector(PB_TAG);
  if (!d) return;
  if (d._skipNextUpdate) { d._skipNextUpdate = false; return; }
  if (d.classList.contains('is-open')) d.refreshFromServer();
});

window.openCartDrawer = () => document.querySelector(PB_TAG)?.open();